import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Shield, AlertTriangle } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/auth/magic-link", { email });
      setSent(true);
      toast.success("Magic link sent! Check your email.");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send magic link";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1714601344981-75e003bc5d18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3NTA0ODYwMnww&ixlib=rb-4.1.0&q=85')" 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B0000]/80 to-[#8B0000]/40"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-bold mb-4">PCM CompliSun Guardian</h1>
          <p className="text-lg opacity-90">AI-Powered Compliance Platform for Pointe Capital Management</p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_50f5a9a4-7203-4eda-9166-5e288543e67f/artifacts/9ya4uhxn_PointeCaptiallargeLogo.png" 
              alt="Pointe Capital Management" 
              className="h-16 mx-auto mb-4"
            />
            <p className="text-slate-500 text-sm">Compliance Portal</p>
          </div>

          {!sent ? (
            <Card className="border-slate-200 shadow-none">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-semibold text-slate-900">Sign In</CardTitle>
                <CardDescription className="text-slate-500">
                  Enter your company email to receive a secure sign-in link
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@pointecapital.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 input-focus"
                        data-testid="login-email-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 text-[#8B0000] mt-0.5 flex-shrink-0" />
                      <span>Access restricted to <strong>@pointecapital.com</strong> and <strong>@complisun.com</strong> email addresses only.</span>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Magic Link"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Check Your Email</h3>
                  <p className="text-slate-600 mb-4">
                    We've sent a magic link to <strong>{email}</strong>
                  </p>
                  <p className="text-sm text-slate-500 mb-6">
                    The link expires in 15 minutes. Click it to sign in securely.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setSent(false)}
                    className="btn-secondary"
                    data-testid="try-another-email-btn"
                  >
                    Try Another Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Important:</strong> This portal contains AI-powered compliance tools. Do not rely solely on AI analysis for compliance decisions. 
                Contact Chief Compliance Officer Colin Darke at <a href="mailto:colindarke@complisun.com" className="underline">colindarke@complisun.com</a> for guidance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
